import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ButtonAdd } from "@/components/ui/button";
import MenuItemOptionDrawer from "@/components/menu/MenuItemOptionDrawer";
import MenuItemOptionDetailsDrawer from "@/components/menu/MenuItemOptionDetailsDrawer";
import { useModal, usePagination } from "@/lib/hooks";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { usePermissions } from "@/hooks/usePermissions";
import { useGetMenuItemOptionsQuery } from "@/redux/services/menu/menu.api";
import { MenuItemOption } from "@/redux/services/menu/menu.type";
import { Skeleton } from "@/components/ui/skeleton";
import ReactPagination from "@/components/Table/Pagination";
import { List, ToggleLeft, CheckSquare, Settings } from "lucide-react";

const typeConfig: Record<string, { label: string; icon: React.FC<{ className?: string }>; color: string }> = {
  select: { label: "Liste déroulante", icon: List, color: "bg-blue-100 text-blue-700" },
  radio: { label: "Bouton radio", icon: ToggleLeft, color: "bg-purple-100 text-purple-700" },
  checkbox: { label: "Case à cocher", icon: CheckSquare, color: "bg-accent-light text-accent" },
};

const TabsOption = () => {
  const { limit, onChange, page } = usePagination(12);
  const { currentRestaurant, currentUser } = useAppSelector(
    (state) => state.user
  );
  const { canManage } = usePermissions();
  const isReadOnly =
    currentUser?.roles?.includes(Roles.owner) || !canManage("menu");
  const { data, isLoading } = useGetMenuItemOptionsQuery({
    limit,
    page,
    restaurantId: currentRestaurant?.id,
  });
  const { isOpen, item, closeModal, openEditModal, openModal } =
    useModal<MenuItemOption>();
  const [detailsOptionId, setDetailsOptionId] = useState<string | null>(null);

  // An option (or option value) picked in the global search lands here as
  // ?option=<id>: open its details, then drop the param (SFRESTO-167).
  const [searchParams, setSearchParams] = useSearchParams();
  const optionParam = searchParams.get("option");
  useEffect(() => {
    if (!optionParam) return;
    setDetailsOptionId(optionParam);
    const next = new URLSearchParams(searchParams);
    next.delete("option");
    setSearchParams(next, { replace: true });
  }, [optionParam]);

  const handleEditFromDetails = (option: MenuItemOption) => {
    setDetailsOptionId(null);
    openEditModal(option);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Skeleton className="w-36 h-8" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {!isReadOnly && (
        <div className="flex justify-end mb-6">
          <ButtonAdd label="Ajouter une option" onClick={openModal} />
        </div>
      )}

      {data?.results && data.results.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.results.map((option) => {
            const type = typeConfig[option.type] ?? typeConfig.select;
            const TypeIcon = type.icon;
            const values = option.menuItemOptionValues ?? [];

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setDetailsOptionId(option.id)}
                className="bg-card rounded-lg border border-border p-4 space-y-3 text-left transition-colors hover:border-primary hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                {/* Header: title + type */}
                <div>
                  <h3 className="font-semibold text-card-foreground">
                    {option.title}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${type.color}`}
                  >
                    <TypeIcon className="h-3 w-3" />
                    {type.label}
                  </span>
                </div>

                {/* Values as pills */}
                {values.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {values.slice(0, 3).map((value) => (
                      <span
                        key={value.id}
                        className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs font-medium text-muted-foreground"
                      >
                        {value.title}
                      </span>
                    ))}
                    {values.length > 3 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted text-xs font-medium text-muted-foreground">
                        +{values.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Footer info */}
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border">
                  <span>
                    {values.length} valeur{values.length !== 1 ? "s" : ""}
                  </span>
                  <span>Position: {option.sortOrder}</span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <Settings className="h-12 w-12 text-muted-foreground opacity-30 mb-3" />
          <p className="text-muted-foreground text-lg mb-2">
            Aucune option
          </p>
          <p className="text-muted-foreground text-sm">
            Cliquez sur "Ajouter une option" pour commencer
          </p>
        </div>
      )}

      {data?.results &&
        data.results.length > 0 &&
        data?.count &&
        (data.count > limit || limit > 12) && (
          <div className="flex-shrink-0 mt-10">
            <ReactPagination
              itemsPerPage={limit}
              count={data.count}
              onChange={onChange}
              currentPage={page}
              options={[
                { value: 12, label: "12" },
                { value: 24, label: "24" },
                { value: 48, label: "48" },
              ]}
            />
          </div>
        )}

      <MenuItemOptionDetailsDrawer
        optionId={detailsOptionId}
        isOpen={!!detailsOptionId}
        onClose={() => setDetailsOptionId(null)}
        onEdit={handleEditFromDetails}
      />

      <MenuItemOptionDrawer item={item} isOpen={isOpen} onClose={closeModal} />
    </>
  );
};

export default TabsOption;
